/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { createContext, useRef, useState, type ReactNode } from "react";
import {
  DismissButton,
  mergeProps,
  Overlay,
  usePopover,
  type AriaPopoverProps,
} from "react-aria";
import type { OverlayTriggerState } from "react-stately";
import { cssFns } from "@superweb/css";
import { usePopoverContainer } from "../../../popover";

import type { ItemSize } from "../types";
import { useOverlayPosition } from "../../../overlay";

type PopoverProps = Omit<AriaPopoverProps, "popoverRef"> & {
  children: ReactNode;
  state: OverlayTriggerState;
  itemSize: ItemSize;
  parentContainer?: HTMLDivElement | null;
};

export const PopoverMaxHeightContext = createContext<number | undefined>(
  undefined,
);

export const PopoverParentContainerContext =
  createContext<HTMLDivElement | null>(null);

export const Popover = ({
  children,
  state,
  itemSize,
  parentContainer,
  ...props
}: PopoverProps) => {
  const popoverContainer = usePopoverContainer();
  const [popoverChildContainer, setPopoverChildContainer] =
    useState<HTMLDivElement | null>(null);

  const popoverRef = useRef<HTMLDivElement>(null);
  const { popoverProps, underlayProps } = usePopover(
    {
      ...props,
      popoverRef,
    },
    state,
  );

  const overlayProps = useOverlayPosition({
    ...props,
    overlayRef: popoverRef,
    targetRef: props.triggerRef,
  });

  const maxHeight = popoverProps.style?.maxHeight
    ? Number(popoverProps.style.maxHeight)
    : undefined;

  return (
    <Overlay
      portalContainer={parentContainer ? parentContainer : popoverContainer}
    >
      <div
        {...underlayProps}
        css={{ position: "fixed", ...cssFns.inset("0") }}
      />
      <div
        {...mergeProps(popoverProps, overlayProps)}
        ref={popoverRef}
        css={{
          width: "max-content",
          minWidth: `${popoverChildContainer?.offsetWidth ?? 0}px`,
        }}
      >
        <div
          ref={(element) => {
            setPopoverChildContainer(element);
          }}
        />
        <PopoverParentContainerContext.Provider value={popoverChildContainer}>
          <PopoverMaxHeightContext.Provider value={maxHeight}>
            <DismissButton onDismiss={state.close} />
            {children}
            <DismissButton onDismiss={state.close} />
          </PopoverMaxHeightContext.Provider>
        </PopoverParentContainerContext.Provider>
      </div>
    </Overlay>
  );
};
